// src/pages/OvertimeRequests.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../navigation/AuthProvider";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import { Toast } from "../../components/ToastPopup";
import Nav from "../../components/nav";
import OvertimeRequestModal from "../../components/OvertimeRequestModal";
import { getPermission } from "../../permissions/rolePermissions";


const PAGE_SIZE = 50;
const formatTime = (t) => (t || "").padStart(5, "0");

// helper: format "Inskickat" (YYYY-MM-DD • HH:mm) from ISO date
const two = (n) => String(n).padStart(2, "0");
const formatSubmitted = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const ymd = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
    const hm = `${two(d.getHours())}:${two(d.getMinutes())}`;
    return `${ymd} • ${hm}`; // tillbaka till dot-format
  } catch {
    return "—";
  }
};

// Column & action sizing so header and rows always align perfectly
const CHECKBOX_COL = 40;       // px
const ACTION_TOTAL_WIDTH = 96; // For the 2 action icon buttons
const ACTION_HEIGHT = 32;
const ACTION_GAP = 8;
const ICON_BTN_WIDTH = (ACTION_TOTAL_WIDTH - ACTION_GAP) / 2;

// Header + Row use the exact same template
const GRID_TEMPLATE = `${CHECKBOX_COL}px 1.4fr 1.6fr 1fr 1fr 1.2fr ${ACTION_TOTAL_WIDTH}px`;

export const OvertimeRequests = () => {
  const {
    user,
    company,
    timeSchemas,
    projects,
    draftSchedule,
    setDraftSchedule,
    overtimeRules = [],
  } = useAuth();

  const employeesPermission = getPermission(user.role, "cases:overtime");
  const canEditOvertimeErrand = employeesPermission === "edit";

  // UI
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");

  // Data
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);

  // Selection
  const [selectedRequests, setSelectedRequests] = useState(new Set());

  // Row modal
  const [activeRequest, setActiveRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reject prompt (single)
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Approve prompt (single) – requires overtime type
  const [showApprovePrompt, setShowApprovePrompt] = useState(false);
  const [approveTargetId, setApproveTargetId] = useState(null);
  const [approveOvertimeType, setApproveOvertimeType] = useState("");

  const [approveFrom, setApproveFrom] = useState("");
  const [approveTo, setApproveTo] = useState("");

  // Restore confirm (single)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreTargetId, setRestoreTargetId] = useState(null);

  // Bulk decision prompt
  const [bulkMode, setBulkMode] = useState(null); // "approve" | "reject" | null
  const [bulkNote, setBulkNote] = useState("");
  const [bulkApproveType, setBulkApproveType] = useState("");

  const monthsInSwedish = [
    "Januari","Februari","Mars","April","Maj","Juni",
    "Juli","Augusti","September","Oktober","November","December"
  ];

  // ---------- Styles ----------
  const styles = {
    wrap: { width: "100%", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
    container: { margin: "0 auto", padding: "24px 32px", maxWidth: 1600 },

    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 24 },
    headerContent: { flex: 1 },
    pageTitle: { margin: 0, fontSize: 32, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.02em" },
    pageSubtitle: { margin: "4px 0 0", fontSize: 16, color: "#64748b", fontWeight: 400 },

    statsContainer: { display: "flex", gap: 16, flexWrap: "wrap" },
    statCard: { background: "white", padding: "16px 20px", borderRadius: 12, border: "1px solid #e2e8f0", minWidth: 140, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    statValue: { fontSize: 24, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 },
    statLabel: { fontSize: 12, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },

    // Controls (date pickers)
    controlsSection: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" },
    dateSelector: { display: "flex", alignItems: "center", gap: 12, background: "white", border: "1px solid #d1d5db", padding: "8px 16px", borderRadius: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },
    select: { background: "white", border: "1px solid #d1d5db", color: "#374151", padding: "6px 8px", borderRadius: 6, outline: "none", fontSize: 14, fontWeight: 500, minWidth: 120 },

    // Bulk bar
    bulkBarShell: { height: 56, marginBottom: 16 },
    bulkActionsHidden: {
      visibility: "hidden",
      opacity: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid transparent",
      background: "transparent",
      pointerEvents: "none",
    },
    bulkActions: {
      visibility: "visible",
      opacity: 1,
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      background: "#fff",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    bulkText: { fontSize: 13, fontWeight: 600, color: "#0f172a" },
    bulkSpacer: { flex: 1 },
    bulkBtn: {
      borderRadius: 6, border: "1px solid #d1d5db",
      background: "white", color: "#374151",
      cursor: "pointer", fontSize: 12, fontWeight: 700,
      height: ACTION_HEIGHT, minWidth: ACTION_TOTAL_WIDTH,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    },

    // Filter bar
    filterBar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "16px 20px", background: "white", borderRadius: 8, border: "1px solid #e5e7eb", marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    input: { flex: 1, minWidth: 280, background: "white", border: "1px solid #d1d5db", color: "#374151", padding: "8px 12px", borderRadius: 6, outline: "none", fontSize: 14, transition: "all 0.2s ease" },

    // Table
    tableContainer: { background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", marginBottom: 24 },
    tableHeader: {
      display: "grid", gridTemplateColumns: GRID_TEMPLATE, gap: 16,
      padding: "14px 20px", color: "#374151", fontSize: 11, fontWeight: 800, background: "#f8fafc",
      borderBottom: "1px solid #e5e7eb", textTransform: "uppercase", letterSpacing: "0.06em",
      alignItems: "start"
    },
    thText: { lineHeight: 1, paddingTop: 2 },

    tableRow: {
      display: "grid", gridTemplateColumns: GRID_TEMPLATE, gap: 16,
      alignItems: "center",
      padding: "12px 20px", borderBottom: "1px solid #f3f4f6",
      cursor: "pointer", transition: "background-color 0.12s ease"
    },
    emptyState: { padding: "48px 20px", color: "#6b7280", fontSize: 14, textAlign: "center" },

    cell: { minWidth: 0 },

    // Checkbox
    checkbox: {
      width: 16, height: 16, borderRadius: 4, border: "2px solid #d1d5db",
      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "center"
    },
    checkboxSelected: { background: "#2563eb", borderColor: "#2563eb", color: "white" },
    checkboxDisabled: { opacity: 0.35, cursor: "not-allowed" },

    // Status badge
    badge: (status) => {
      const base = { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, lineHeight: 1, border: "1px solid transparent" };
      if (status === "approved") return { ...base, background: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" };
      if (status === "rejected") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
      return { ...base, background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" };
    },

    // Action buttons
    btnBase: {
      borderRadius: 6, border: "1px solid transparent",
      background: "white", color: "#374151",
      cursor: "pointer", fontSize: 14, fontWeight: 700,
      height: ACTION_HEIGHT, display: "inline-flex",
      alignItems: "center", justifyContent: "center",
    },
    iconBtn: { width: ICON_BTN_WIDTH, padding: 0 },
    approveBtn: { background: "#16a34a", borderColor: "#16a34a", color: "white" },
    rejectBtn:  { background: "#ef4444", borderColor: "#ef4444", color: "white" },
    restoreBtn: { minWidth: ACTION_TOTAL_WIDTH, border: "1px solid #d1d5db", color: "#374151", background: "white", fontSize: 12 },

    // Shift cell content
    shiftProject: { display: "block", fontWeight: 700, color: "#0f172a", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    shiftTime: { display: "block", fontSize: 13, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

    // Pagination
    pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    paginationInfo: { fontSize: 14, color: "#6b7280", fontWeight: 500 },
    paginationControls: { display: "flex", alignItems: "center", gap: ACTION_GAP },
    // ✅ DESIGN FIX: ensure button text is perfectly centered
    pageBtn: {
      borderRadius: 6,
      border: "1px solid #d1d5db",
      background: "white",
      color: "#374151",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      height: ACTION_HEIGHT,
      minWidth: ACTION_TOTAL_WIDTH,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
      padding: "0 12px",
      textAlign: "center",
      whiteSpace: "nowrap",
    },

    // Prompts (shared)
    promptBackdrop: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 20
    },
    promptBox: {
      width: "100%", maxWidth: 460, background: "#fff", borderRadius: 12,
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", padding: 18, border: "1px solid #e5e7eb"
    },
    promptTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" },
    promptP: { marginTop: 6, fontSize: 13, color: "#6b7280" },
    promptTextarea: {
      width: "100%", marginTop: 10, borderRadius: 8, border: "1px solid #d1d5db",
      padding: "10px 12px", fontSize: 14, outline: "none", resize: "vertical", minHeight: 90
    },
    promptActions: { display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" },
    promptBtnGhost: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#111827", fontWeight: 700, cursor: "pointer"
    },
    promptBtnDanger: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #ef4444", background: "#ef4444", color: "#fff", fontWeight: 700, cursor: "pointer"
    },
    promptBtnSuccess: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #16a34a", background: "#16a34a", color: "#fff", fontWeight: 700, cursor: "pointer"
    },
    promptBtnPrimary: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #2563eb", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer"
    },
  };

  // ---------- Data fetching ----------
  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/overtime-requests`, {
        params: {
          userId: user._id,
          companyId: company._id,
          month: selectedMonth,
          year: selectedYear,
          status: statusFilter === "all" ? undefined : statusFilter,
          timeSchemaId: selectedSchema || undefined,
        },
      });
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (err) {
      console.error("Kunde inte hämta övertidsansökningar:", err);
      setToastType("error");
      setToastMessage(err.response?.data?.message || "Kunde inte hämta övertidsansökningar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company._id, user._id, selectedMonth, selectedYear, statusFilter, selectedSchema]);

  // ---------- Local draftSchedule index ----------
  const draftById = useMemo(() => {
    const map = new Map();
    (draftSchedule || []).forEach((s) => {
      if (s && s._id) map.set(String(s._id), s);
    });
    return map;
  }, [draftSchedule]);

  const getTimeSchemaIdForRequest = (r) => {
    const apiTs = r?.shiftTimeSchema || r?.timeSchema;
    if (apiTs) return String(apiTs);
    const shiftId = r?.shift;
    if (!shiftId) return null;
    const shift = draftById.get(String(shiftId));
    const ts = shift?.timeSchema || shift?.timeSchemaId;
    return ts ? String(ts) : null;
  };

  // ---------- Derived data ----------
  const schemaFiltered = useMemo(() => {
    if (!selectedSchema) return requests;
    return requests.filter((r) => {
      const ts = getTimeSchemaIdForRequest(r);
      return ts ? ts === String(selectedSchema) : true;
    });
  }, [requests, selectedSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    // 🔐 Se till att bara visa ansökningar där createdAt ligger i vald månad/år
    const monthFiltered = schemaFiltered.filter((r) => {
      if (!r?.createdAt) return true; // gamla/konstiga records släpps igenom
      const d = new Date(r.createdAt);
      if (isNaN(d.getTime())) return true;
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      return month === selectedMonth && year === selectedYear;
    });

    if (!q) return monthFiltered;

    return monthFiltered.filter((r) => {
      const who    = (r.employeeName || r.employeeEmail || "").toLowerCase();
      const reason = (r.reason || "").toLowerCase();
      const status = (r.status || "").toLowerCase();
      const dateStr= (r.shiftDate || "").toLowerCase();

      // 1) ev. fält från API om det råkar finnas
      const projectFromReq = (r.projectName || "").toLowerCase();

      // 2) säkert härlett projektnamn från draftSchedule + projects
      const projectFromShift = (() => {
        try {
          const shift = (draftSchedule || []).find(s => String(s._id) === String(r.shift));
          if (!shift) return "";
          const projId = shift.project || shift.projectId;
          const proj = (projects || []).find(p => String(p._id) === String(projId));
          return (proj?.name || proj?.title || "").toLowerCase();
        } catch {
          return "";
        }
      })();

      return (
        who.includes(q) ||
        reason.includes(q) ||
        status.includes(q) ||
        dateStr.includes(q) ||
        projectFromReq.includes(q) ||
        projectFromShift.includes(q)
      );
    });
    // Viktigt: lägg till beroenden så filtret körs om listor byts
  }, [schemaFiltered, searchQuery, draftSchedule, projects, selectedMonth, selectedYear]);


  const stats = useMemo(() => {
    const total = filteredRequests.length;
    const pending = filteredRequests.filter(r => r.status === 'pending').length;
    const approved = filteredRequests.filter(r => r.status === 'approved').length;
    const rejected = filteredRequests.filter(r => r.status === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [filteredRequests]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRequests.slice(start, start + PAGE_SIZE);
  }, [filteredRequests, page]);

  useEffect(() => {
    setPage(1);
    setSelectedRequests(new Set());
  }, [searchQuery, selectedSchema, selectedMonth, selectedYear, statusFilter]);

  // ---------- Draft helpers ----------
  const upsertShiftInDraft = (updatedShift) => {
    if (!updatedShift || !updatedShift._id) return;
    setDraftSchedule((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const id = String(updatedShift._id);
      const idx = prevArr.findIndex((s) => String(s._id) === id);
      if (idx === -1) {
        return [...prevArr, updatedShift];
      }
      const copy = [...prevArr];
      copy[idx] = { ...copy[idx], ...updatedShift };
      return copy;
    });
  };

  const mergeManyShiftsInDraft = (shiftsArr = []) => {
    const valid = shiftsArr.filter((s) => s && s._id);
    if (valid.length === 0) return;
    setDraftSchedule((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const byId = new Map(prevArr.map((s) => [String(s._id), s]));
      for (const incoming of valid) {
        const id = String(incoming._id);
        const existing = byId.get(id);
        byId.set(id, existing ? { ...existing, ...incoming } : incoming);
      }
      return Array.from(byId.values());
    });
  };

  // ---------- Selection & Actions ----------
  const isPending = (r) => r.status === "pending";

  const toggleSelectRequest = (requestId) => {
    const target = requests.find(x => x._id === requestId);
    if (target && !isPending(target)) return;
    const next = new Set(selectedRequests);
    if (next.has(requestId)) next.delete(requestId);
    else next.add(requestId);
    setSelectedRequests(next);
  };

  const everyIdSelected = (ids) => ids.length && ids.every(id => selectedRequests.has(id));

  const toggleSelectAll = () => {
    const onlyPendingIds = paged.filter(isPending).map(r => r._id);
    if (onlyPendingIds.length && everyIdSelected(onlyPendingIds)) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(onlyPendingIds));
    }
  };

  const decideRequest = async (reqId, action, note, overtimeRuleId, overrideFrom, overrideTo) => {
    const payload = {
      userId: user._id,
      companyId: company._id,
      requestId: reqId,
      action,
      note,
    };

    if (action === "approve") {
      payload.overtimeRuleId = overtimeRuleId;
      if (overrideFrom) payload.overrideFrom = overrideFrom;
      if (overrideTo) payload.overrideTo = overrideTo;
    }

    const { data } = await axios.post(`${BACKEND_URL}/overtime-requests/decide`, payload);
    return data;
  };


  const runBulk = async (action, note, overtimeRuleId) => {
    if (selectedRequests.size === 0) return;
    setLoading(true);
    try {
      const ids = Array.from(selectedRequests);
      const updatedShifts = [];
      for (const id of ids) {
        const res = await decideRequest(id, action, note, overtimeRuleId);
        if (res?.shift) updatedShifts.push(res.shift);
      }
      if (updatedShifts.length) mergeManyShiftsInDraft(updatedShifts);

      setToastType("success");
      setToastMessage(
        action === "approve"
          ? `${ids.length} ansökningar godkända.`
          : `${ids.length} ansökningar nekade.`
      );
      setSelectedRequests(new Set());
      await loadRequests();
    } catch (err) {
      console.error("Bulk decide failed:", err);
      setToastType("error");
      setToastMessage("Kunde inte uppdatera alla valda ansökningar.");
    } finally {
      setLoading(false);
    }
  };

  // ----- Single reject -----
  const openRejectPrompt = (reqId) => {
    setRejectTargetId(reqId);
    setRejectReason("");
    setShowRejectPrompt(true);
  };
  const closeRejectPrompt = () => {
    setShowRejectPrompt(false);
    setRejectTargetId(null);
    setRejectReason("");
  };
  const confirmReject = async () => {
    if (!rejectTargetId) return;
    try {
      setLoading(true);
      await decideRequest(rejectTargetId, "reject", rejectReason.trim() || undefined);
      setToastType("success");
      setToastMessage("Ansökan nekad.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setIsModalOpen(false)
      setLoading(false);
      closeRejectPrompt();
    }
  };

  const openApprovePrompt = (reqId) => {
    setApproveTargetId(reqId);
    setApproveOvertimeType(""); // tom default

    // NEW: hitta requesten och förifyll tider
    const req = requests.find((r) => r._id === reqId);
    setApproveFrom(req?.from || "");
    setApproveTo(req?.to || "");

    setShowApprovePrompt(true);
  };

  const closeApprovePrompt = () => {
    setShowApprovePrompt(false);
    setApproveTargetId(null);
    setApproveOvertimeType("");
    setApproveFrom("");
    setApproveTo("");
  };

  const confirmApprove = async () => {
    if (!approveTargetId) return;
    if (!approveOvertimeType) {
      setToastType("error");
      setToastMessage("Välj en övertidstyp.");
      return;
    }

    const from = (approveFrom || "").trim();
    const to = (approveTo || "").trim();
    const hhmm = /^\d{1,2}:\d{2}$/;

    if (!hhmm.test(from) || !hhmm.test(to) || from === to) {
      setToastType("error");
      setToastMessage("Ogiltiga tider. Ange Från/Till i formatet HH:MM och se till att de inte är samma.");
      return;
    }

    try {
      setLoading(true);
      const res = await decideRequest(
        approveTargetId,
        "approve",
        undefined,
        approveOvertimeType,
        from,
        to
      );
      if (res?.shift) upsertShiftInDraft(res.shift);
      setToastType("success");
      setToastMessage("Ansökan godkänd.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setLoading(false);
      setIsModalOpen(false);
      closeApprovePrompt();
    }
  };


  // ----- Restore (single) -----
  const openRestoreConfirm = (reqId) => { setRestoreTargetId(reqId); setShowRestoreConfirm(true); };
  const closeRestoreConfirm = () => { setShowRestoreConfirm(false); setRestoreTargetId(null); };
  const confirmRestore = async () => {
    if (!restoreTargetId) return;
    try {
      setLoading(true);
      const res = await decideRequest(restoreTargetId, "restore");
      if (res?.shift) upsertShiftInDraft(res.shift);
      setToastType("success");
      setToastMessage("Ansökan återställd.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setIsModalOpen(false)
      setLoading(false);
      closeRestoreConfirm();
    }
  };

  // ----- Bulk prompt handlers -----
  const openBulkApprove = () => {
    if (!selectedRequests.size) return;
    setBulkMode("approve");
    setBulkApproveType(""); // tom default
    setBulkNote("");
  };
  const openBulkReject  = () => { if (selectedRequests.size) { setBulkMode("reject");  setBulkNote(""); } };
  const closeBulkPrompt = () => { setBulkMode(null); setBulkNote(""); setBulkApproveType(""); };

  const confirmBulk = async () => {
    if (!bulkMode) return;
    if (bulkMode === "approve") {
      if (!bulkApproveType) {
        setToastType("error");
        setToastMessage("Välj en övertidstyp för bulk-godkänning.");
        return;
      }
      await runBulk("approve", undefined, bulkApproveType);
    } else {
      await runBulk("reject", bulkNote.trim() || undefined);
    }
    closeBulkPrompt();
  };

  // Row details modal
  const openModal = (req) => { setActiveRequest(req); setIsModalOpen(true); };
  const closeModal = () => { setActiveRequest(null); setIsModalOpen(false); };

  // Helper: safe project name for table cell
  const getProjectNameForShift = (shiftId) => {
    try {
      const shift = (draftSchedule || []).find((s) => String(s._id) === String(shiftId));
      if (!shift) return "—";
      const projId = shift.project || shift.projectId;
      const proj = (projects || []).find((p) => String(p._id) === String(projId));
      return proj?.name || proj?.title || "—";
    } catch {
      return "—";
    }
  };

  return (
    <div style={styles.wrap}>
      <Nav/>
      <div style={styles.container}>
        {/* Header with Stats */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.pageTitle}>Övertidsansökningar</h1>
            <p style={styles.pageSubtitle}>Administrera och granska övertidsansökningar i realtid</p>

            <div style={styles.statsContainer}>
              <div style={styles.statCard}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>Totalt</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#92400e"}}>{stats.pending}</div><div style={styles.statLabel}>Väntar</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#166534"}}>{stats.approved}</div><div style={styles.statLabel}>Godkända</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#991b1b"}}>{stats.rejected}</div><div style={styles.statLabel}>Nekade</div></div>
            </div>
          </div>
        </div>

        {/* Controls (Month / Year) */}
        <div style={styles.controlsSection}>
          <div style={styles.dateSelector}>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(+e.target.value)} style={styles.select}>
              {monthsInSwedish.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(+e.target.value)} style={styles.select}>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div />
        </div>

        {/* Bulk bar */}
        <div style={styles.bulkBarShell}>
          <div style={selectedRequests.size > 0 ? styles.bulkActions : styles.bulkActionsHidden}>
            <span style={styles.bulkText}>
              {selectedRequests.size} valda
            </span>
            <div style={styles.bulkSpacer} />
            <button onClick={openBulkApprove} style={styles.bulkBtn}>Godkänn</button>
            <button onClick={openBulkReject}  style={styles.bulkBtn}>Neka</button>
            <button onClick={() => setSelectedRequests(new Set())} style={styles.bulkBtn}>Rensa</button>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filterBar}>
          <input
            style={styles.input}
            type="text"
            placeholder="Sök ansökningar, namn, datum, projekt eller orsak..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select value={selectedSchema} onChange={(e) => setSelectedSchema(e.target.value)} style={styles.select}>
            <option value="">Alla scheman</option>
            {timeSchemas.map((s) => (<option key={s._id} value={s._id}>{s.name}</option>))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
            <option value="all">Alla status</option>
            <option value="pending">Väntar</option>
            <option value="approved">Godkända</option>
            <option value="rejected">Nekade</option>
          </select>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
            
            <div
              style={{
                ...styles.checkbox,
                ...(!canEditOvertimeErrand ? styles.checkboxDisabled : {})
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!canEditOvertimeErrand) return;
                toggleSelectAll();
              }}
              title={
                canEditOvertimeErrand
                  ? "Markera alla väntande på sidan"
                  : "Du har endast läsbehörighet"
              }
            />

            <span style={styles.thText}>Sökande</span>
            <span style={styles.thText}>Skift</span>
            <span style={styles.thText}>Övertid</span>
            <span style={styles.thText}>Status</span>
            <span style={styles.thText}>Orsak</span>
            {canEditOvertimeErrand && (<span style={styles.thText}>Åtgärd</span>)}
            
          </div>

        {loading ? (
          <div style={styles.emptyState}>Laddar övertidsansökningar...</div>
        ) : paged.length === 0 ? (
          <div style={styles.emptyState}>
            {searchQuery || selectedSchema || statusFilter !== "all"
              ? "Inga ansökningar matchar dina sökkriterier."
              : "Inga övertidsansökningar för valt datumintervall."}
          </div>
        ) : (
          paged.map((r) => {
            const pending = r.status === "pending";
            return (
              <div
                key={r._id}
                style={styles.tableRow}
                onClick={() => openModal(r)}
              >
                {/* Checkbox */}
                <div
                  onClick={(e) => { e.stopPropagation(); if (!canEditOvertimeErrand) return; if (pending) toggleSelectRequest(r._id); }}
                  style={{
                    ...styles.checkbox,
                    ...(!canEditOvertimeErrand || !pending
                      ? styles.checkboxDisabled
                      : selectedRequests.has(r._id)
                        ? styles.checkboxSelected
                        : {}),

                  }}
                  title={pending ? (selectedRequests.has(r._id) ? "Avmarkera" : "Markera") : "Redan beslutad"}
                >
                  {pending && selectedRequests.has(r._id) ? "✓" : ""}
                </div>

                {/* Sökande: namn + "Inskickat YYYY-MM-DD • HH:mm" */}
                <span style={{ ...styles.cell, lineHeight: 1.25 }}>
                  <strong style={styles.shiftProject}>
                    {r.employeeName || r.employeeEmail || "—"}
                  </strong>
                  <span style={styles.shiftTime}>
                    Inskickad: <b style={{color: "#47494eff", fontSize: 14}}>{formatSubmitted(r.createdAt)}</b>
                  </span>
                </span>

                {/* Skift (behåller ellipsis och utan ändringar i separatorn här) */}
                <span style={{ ...styles.cell, lineHeight: 1.25 }}>
                  <strong style={styles.shiftProject}>{getProjectNameForShift(r.shift)}</strong>
                  <span style={styles.shiftTime}>
                    {r.shiftDate && r.shiftStart && r.shiftEnd ? `${r.shiftDate} ${r.shiftStart}-${r.shiftEnd}` : "—"}
                  </span>
                </span>

                {/* Övertid */}
                <span style={{ ...styles.cell, fontSize: 13, color: "#64748b", fontWeight: 600 }}>
                  {formatTime(r.from)} – {formatTime(r.to)}
                </span>

                {/* Status */}
                <span style={styles.cell}>
                  <span style={styles.badge(r.status)}>
                    {r.status === "approved" ? "Godkänd" : r.status === "rejected" ? "Nekad" : "Väntar"}
                  </span>
                </span>

                {/* Orsak */}
                <span style={{ ...styles.cell, fontSize: 13, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.reason || "—"}
                </span>

                {/* Åtgärd */}
                <div
                  style={{ display: "flex", gap: ACTION_GAP, justifyContent: "flex-end", alignItems: "center", width: ACTION_TOTAL_WIDTH }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {canEditOvertimeErrand && (
                    <>
                      {pending ? (
                      <>
                        <button
                          onClick={() => openApprovePrompt(r._id)}
                          style={{ ...styles.btnBase, ...styles.iconBtn, ...styles.approveBtn }}
                          title="Godkänn"
                          aria-label="Godkänn"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => openRejectPrompt(r._id)}
                          style={{ ...styles.btnBase, ...styles.iconBtn, ...styles.rejectBtn }}
                          title="Neka"
                          aria-label="Neka"
                        >
                          ✕
                        </button>
                      </>
                    ) :  (
                      <button
                        onClick={() => openRestoreConfirm(r._id)}
                        style={{ ...styles.btnBase, ...styles.restoreBtn }}
                        title="Återställ till väntande"
                      >
                        Återställ
                      </button>
                    )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Visar {Math.min(PAGE_SIZE, paged.length)} av {filteredRequests.length} ansökningar
            </div>
            <div style={styles.paginationControls}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}
              >
                Föregående
              </button>
              <span style={{ fontSize: 14, color: "#6b7280", padding: "0 16px" }}>
                Sida {page} av {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}
              >
                Nästa
              </button>
            </div>
          </div>
        )}

        {/* Row details modal */}
        {isModalOpen && activeRequest && (
          <OvertimeRequestModal
            open={isModalOpen}
            request={activeRequest}
            onClose={closeModal}
            canDecide={user.role === "admin" || user.role === "super-admin"}
            onApprove={() => openApprovePrompt(activeRequest._id)}
            onReject={() => openRejectPrompt(activeRequest._id)}
            isReadOnly={!canEditOvertimeErrand}
          />
        )}

        {/* Reject prompt (single) */}
        {showRejectPrompt && (
          <div style={styles.promptBackdrop} onClick={closeRejectPrompt}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Anledning till nekning</h3>
              <p style={styles.promptP}>Valfritt: skriv en kort kommentar som förklarar varför ansökan nekas.</p>
              <textarea
                style={styles.promptTextarea}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Skriv anledning (valfritt)…"
              />
              <div style={styles.promptActions}>
                <button onClick={closeRejectPrompt} style={styles.promptBtnGhost}>Avbryt</button>
                <button onClick={confirmReject} style={styles.promptBtnDanger}>Bekräfta nekning</button>
              </div>
            </div>
          </div>
        )}

        {/* Approve prompt (single) — choose overtime type & optionally adjust times */}
        {showApprovePrompt && (
          <div style={styles.promptBackdrop} onClick={closeApprovePrompt}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Godkänn övertid</h3>
              {(!overtimeRules || overtimeRules.length === 0) ? (
                <p style={styles.promptP}>Inga övertidsregler hittades. Lägg till en regel först.</p>
              ) : (
                <>
                  <p style={styles.promptP}>
                    Välj övertidstyp och justera vid behov vilka tider som ska registreras som övertid.
                  </p>

                  {/* Välj övertidstyp */}
                  <select
                    style={{ ...styles.select, width: "100%", marginTop: 8 }}
                    value={approveOvertimeType}
                    onChange={(e) => setApproveOvertimeType(e.target.value)}
                  >
                    <option value="">Välj övertidstyp…</option>
                    {overtimeRules.map((r) => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>

                  {/* NEW: Från/Till */}
                  <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginRight: 10 }}>Från (HH:MM)</label>
                      <input
                        type="time"
                        value={approveFrom}
                        onChange={(e) => setApproveFrom(e.target.value)}
                        placeholder="t.ex. 18:00"
                        style={{ ...styles.input, marginTop: 4, minWidth: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginRight: 10 }}>Till (HH:MM)</label>
                      <input
                        type="time"
                        value={approveTo}
                        onChange={(e) => setApproveTo(e.target.value)}
                        placeholder="t.ex. 20:30"
                        style={{ ...styles.input, marginTop: 4, minWidth: 0 }}
                      />
                    </div>
                  </div>
                </>
              )}

              <div style={styles.promptActions}>
                <button onClick={closeApprovePrompt} style={styles.promptBtnGhost}>Avbryt</button>
                <button
                  onClick={confirmApprove}
                  style={{
                    ...styles.promptBtnSuccess,
                    opacity: (!overtimeRules?.length || !approveOvertimeType) ? 0.6 : 1
                  }}
                  disabled={!overtimeRules?.length || !approveOvertimeType}
                >
                  Godkänn
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Restore confirm (single) */}
        {showRestoreConfirm && (
          <div style={styles.promptBackdrop} onClick={closeRestoreConfirm}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Bekräfta återställning</h3>
              <p style={styles.promptP}>Vill du återställa denna ansökan till väntande?</p>
              <div style={styles.promptActions}>
                <button onClick={closeRestoreConfirm} style={styles.promptBtnGhost}>Avbryt</button>
                <button onClick={confirmRestore} style={styles.promptBtnPrimary}>Återställ</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk decision prompt */}
        {bulkMode && (
          <div style={styles.promptBackdrop} onClick={closeBulkPrompt}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>
                {bulkMode === "approve" ? "Bulk-godkänning" : "Bekräfta bulk-nekning"}
              </h3>

              {bulkMode === "approve" ? (
                <>
                  {(!overtimeRules || overtimeRules.length === 0) ? (
                    <p style={styles.promptP}>Inga övertidsregler hittades. Lägg till en regel först.</p>
                  ) : (
                    <>
                      <p style={styles.promptP}>
                        Du är på väg att godkänna {selectedRequests.size} st ansökningar.
                        Välj vilken övertidstyp som ska användas för samtliga.
                      </p>
                      <select
                        style={{ ...styles.select, width: "100%", marginTop: 8 }}
                        value={bulkApproveType}
                        onChange={(e) => setBulkApproveType(e.target.value)}
                      >
                        <option value="">Välj övertidstyp…</option>
                        {overtimeRules.map((r) => (
                          <option key={r._id} value={r._id}>{r.name}</option>
                        ))}
                      </select>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p style={styles.promptP}>
                    Du är på väg att neka {selectedRequests.size} st ansökningar. Valfritt: ange en gemensam anledning som sparas på samtliga.
                  </p>
                  <textarea
                    style={styles.promptTextarea}
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                    placeholder="Skriv gemensam anledning (valfritt)…"
                  />
                </>
              )}

              <div style={styles.promptActions}>
                <button onClick={closeBulkPrompt} style={styles.promptBtnGhost}>Avbryt</button>
                {bulkMode === "approve" ? (
                  <button
                    onClick={confirmBulk}
                    style={{ ...styles.promptBtnSuccess, opacity: (!overtimeRules?.length || !bulkApproveType) ? 0.6 : 1 }}
                    disabled={!overtimeRules?.length || !bulkApproveType}
                  >
                    Bekräfta
                  </button>
                ) : (
                  <button onClick={confirmBulk} style={styles.promptBtnDanger}>Bekräfta nekning</button>
                )}
              </div>
            </div>
          </div>
        )}

        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage("")} />
      </div>
    </div>
  );
};

export default OvertimeRequests;
